/**
 * A generic ordered tree built from a flat list of nodes linked by `parent_id`.
 *
 * This logic used to live in `$lib/posts/tree` back when help, blog, and
 * newsletters were all rows in the `posts` table. Help has since moved to files
 * (its nav is the only remaining consumer), so the tree logic lives here, free
 * of any `posts` vocabulary or DB-table coupling — a node is just an `id` and
 * an optional `parent_id`.
 */
export type TreeNode = { id: string; parent_id: string | null }

export type Tree<T extends TreeNode> = (T & {
  children: Tree<T>
})[]

/** Nest a flat list of nodes into a tree by their `parent_id`. */
export const buildTree = <T extends TreeNode>(
  nodes: T[],
  parentId: string | null = null,
): Tree<T> =>
  nodes
    .filter((node) => node.parent_id === parentId)
    .map((node) => ({ ...node, children: buildTree(nodes, node.id) }))

/** Returns the list the node (needle) is inside, plus the node itself. */
const findContainingList = <T extends TreeNode>(
  tree: Tree<T>,
  nodeId: string,
): [Tree<T>, Tree<T>[number]] | undefined => {
  for (const node of tree) {
    if (node.id === nodeId) {
      return [tree, node]
    }
    if (node.children.length > 0) {
      const list = findContainingList(node.children, nodeId)
      if (list) return list
    }
  }
  return undefined
}

/**
 * The node immediately before/after `nodeId` in a depth-first walk of the tree:
 * descend into children going `next`, climb to the parent's neighbour at the
 * edges. Drives the help "up next" links.
 */
export const getRelated = <T extends TreeNode>(
  fullTree: Tree<T>,
  nodeId: string | null,
  direction: 'prev' | 'next',
  deep = false,
): T | undefined => {
  if (!nodeId) return undefined
  const tuple = findContainingList(fullTree, nodeId)
  if (!tuple) return undefined
  const [tree, node] = tuple

  if (!deep && direction === 'next' && node.children.length > 0) {
    return node.children[0]
  }

  const index = tree.findIndex((n) => n.id === node.id)

  const sibling = tree[direction === 'prev' ? index - 1 : index + 1]

  if (sibling) return sibling

  if (!deep && direction === 'prev' && node.parent_id) {
    return findContainingList(fullTree, node.parent_id)?.[1]
  }

  // There was no previous or next node in the current list, so we were on the
  // first or last node respectively — see if the parent has an appropriate one.
  return getRelated(fullTree, node.parent_id, direction, true)
}
